import {defineSlotRecipe} from '@chakra-ui/react3'

export const navigationLinkRecipe = defineSlotRecipe({
    className: 'navigation-link',
    slots: ['root', 'title', 'imageContainer', 'content', 'copy', 'price'],
    base: {
        root: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            textDecoration: 'none',
            borderBottom: 'none',
            borderBottomWidth: 0,
            outline: 'none',
            boxShadow: 'none',
            marginInline: 0,
            // Suppress the shared link underline pseudo-element for navigation rows.
            '& span::after, & p::after': {
                display: 'none'
            }
        }
    },
    variants: {
        variant: {
            link: {
                root: {
                    flexDirection: 'row',
                    paddingTop: 'spacing-4',
                    paddingBottom: 'spacing-4',
                    paddingRight: 0,
                    justifyContent: 'space-between',
                    backgroundColor: 'transparent'
                },
                title: {
                    textStyle: 'heading.medium',
                    color: {base: 'text.primary', s: 'text.tertiary'},
                    _groupHover: {
                        color: 'text.primary'
                    },
                    _groupFocus: {
                        color: 'text.primary'
                    }
                }
            },
            card: {
                root: {
                    flexDirection: 'row',
                    gap: 'spacing-12',
                    paddingX: 'spacing-12',
                    paddingY: 'spacing-6',
                    borderRadius: 's',
                    backgroundColor: 'background.section.subtle',
                    border: '1px solid',
                    borderColor: 'transparent',
                    transition:
                        'background-color 0.2s ease, border-color 0.2s ease, border-radius 0.2s ease',
                    _hover: {
                        backgroundColor: 'background.section.default',
                        borderColor: 'transparent'
                    },
                    _active: {
                        backgroundColor: 'background.section.default'
                    },
                    _focus: {
                        outline: 'none',
                        backgroundColor: 'background.section.default'
                    },
                    _focusVisible: {
                        outline: 'none',
                        backgroundColor: 'background.section.default',
                        borderRadius: '0.625rem',
                        borderColor: 'border.strong',
                        paddingInline: 'spacing-12'
                    }
                },
                title: {
                    textStyle: 'body.small',
                    color: 'text.primary',
                    alignSelf: 'stretch',
                    lineClamp: 1
                },
                imageContainer: {
                    display: 'flex',
                    width: '3.75rem',
                    height: '3.75rem',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0,
                    aspectRatio: '1/1',
                    borderRadius: 's',
                    overflow: 'hidden',
                    backgroundColor: 'background.section.subtle',
                    transition: 'background-color 0.2s ease',
                    _groupHover: {
                        backgroundColor: 'background.section.default'
                    },
                    _groupFocus: {
                        backgroundColor: 'background.section.default'
                    },
                    _groupActive: {
                        backgroundColor: 'background.section.default'
                    }
                },
                content: {
                    flex: 1,
                    minWidth: 0
                },
                copy: {
                    textStyle: 'body.xsmall',
                    color: 'text.secondary',
                    lineClamp: 2,
                    alignSelf: 'stretch'
                },
                price: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    gap: '0',
                    paddingTop: 'spacing-4',
                    textStyle: 'body.xsmall',
                    color: 'text.secondary'
                }
            }
        },
        // The CMS accent flag, taken as authored. The schema types it as the
        // string form of a boolean while the JSON template emits it unquoted, so
        // both shapes reach the recipe. The styling lives in the compound variant
        // below, which pairs the flag with the variant it applies to; the axis is
        // declared here to keep `accentColor` a recipe prop, so it is split off
        // the props rather than reaching the DOM.
        accentColor: {
            false: {},
            true: {}
        }
    },
    compoundVariants: [
        // Accent colour applies to the `link` variant alone. Compound variants
        // match by strict equality, so both authored shapes of the flag are
        // listed.
        //
        // The hover and focus colours are restated because this variant's title
        // sets its own — unlike `NavigationParent`, whose title inherits — and
        // `_groupHover` is the more specific selector, so a base colour alone
        // would lose the accent as soon as the row is hovered or focused.
        {
            variant: 'link',
            accentColor: [true, 'true'],
            css: {
                title: {
                    color: 'text.brand-default',
                    _groupHover: {
                        color: 'text.brand-default'
                    },
                    _groupFocus: {
                        color: 'text.brand-default'
                    }
                }
            }
        }
    ],
    defaultVariants: {
        variant: 'link',
        accentColor: false
    }
})
